import { TextField, Tooltip } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import colorConvert from 'color-convert';
import { areIntervalsOverlapping, differenceInCalendarDays, format, isAfter } from 'date-fns';
import { de, enUS } from 'date-fns/locale';
import { useTranslation } from 'next-i18next';
import React, { useEffect, useState } from 'react';
import { shallowEqual } from 'react-redux';
import { Slot } from '../../../../../../interfaces/interfaces';
import { PresentationComponentInterface } from '../../../../../../interfaces/pagebuilder';
import { useSelector } from '../../../../../../reduxToolkit/typedRedux';
import { formatUTCDate } from '../../../../../../services/helper';
import { dateToTimeline } from '../../../../../../utils/functions';
interface PresentationTimelineInterface {
  uiComponent?: PresentationComponentInterface;
}
const PresentationTimeline = ({ uiComponent }: PresentationTimelineInterface) => {
  const { event, slots } = useSelector(({ common }) => ({ event: common.event, slots: common.slots }), shallowEqual);
  const [timeInterval, setTimeInterval] = useState<{ startTime: number; endTime: number }>({ startTime: 0, endTime: 0 });
  const [slotsGroupedByTime, setSlotsGroupedByTime] = useState<Record<string, Slot[]>>();
  const [slotPositions, setSlotPositions] = useState<number[]>();
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [currDaySlots, setCurrDaySlots] = useState<Slot[]>([]);
  const { t, i18n } = useTranslation('slots');

  if (!event) return <></>;

  function makeGroupName(slot: Slot) {
    return `${formatUTCDate(new Date(slot.datetime_start), 'HH:mm')} - ${formatUTCDate(new Date(slot.datetime_end), 'HH:mm')}`;
  }

  function groupSlotsByTime(slots: Slot[]) {
    return slots.reduce((j: Record<string, Slot[]>, slot) => {
      const group = makeGroupName(slot);
      j.hasOwnProperty(group) ? j[group].push(slot) : (j[group] = [slot]);
      return j;
    }, {});
  }

  useEffect(() => {
    if (currDaySlots.length > 0) {
      const startTime = dateToTimeline(8, true, currDaySlots[0].datetime_start);
      const endTime = dateToTimeline(20, false, currDaySlots[currDaySlots.length - 1].datetime_end);
      setTimeInterval({ startTime, endTime });
      setSlotsGroupedByTime(groupSlotsByTime(currDaySlots));
    }
  }, [currDaySlots]);
  useEffect(() => {
    if (!event) return;
    setSelectedDate(isAfter(Date.now(), new Date(event.datetime_start)) ? new Date() : new Date(event.datetime_start));
  }, [event]);
  useEffect(() => {
    if (!slots || slots.length === 0) return;
    const _slots = [
      ...slots
        .filter(
          (slot) =>
            !uiComponent?.selectedPresentations || uiComponent.selectedPresentations.length === 0 || uiComponent?.selectedPresentations.includes(slot._id)
        )
        .filter((s) => differenceInCalendarDays(selectedDate || new Date(), new Date(s.datetime_start)) === 0),
    ];
    setCurrDaySlots(_slots);
  }, [slots, uiComponent?.selectedPresentations, selectedDate]);

  useEffect(() => {
    if (slotsGroupedByTime) {
      const arr: number[] = [];

      const slotIds = currDaySlots.map((s) => s._id);
      arr.push(0);
      currDaySlots.slice(1, currDaySlots.length).forEach((s, i) => {
        let currP = 0;
        const _overlappingPositions: number[] = currDaySlots
          .slice(0, i + 1)
          .filter((_s) =>
            areIntervalsOverlapping(
              { start: new Date(_s.datetime_start), end: new Date(_s.datetime_end) },
              { start: new Date(s.datetime_start), end: new Date(s.datetime_end) }
            )
          )
          .map((_s) => arr[slotIds.indexOf(_s._id)]);
        _overlappingPositions
          .slice()
          .sort((a, b) => a - b)
          .every((p, i) => {
            const cond = p === i;
            if (cond) currP++;
            return cond;
          });
        arr.push(currP);
      });

      setSlotPositions([...arr]);
    }
  }, [slotsGroupedByTime]);

  function displaySlots() {
    if (currDaySlots && slotsGroupedByTime && slotPositions) {
      const slotWidth = `${100 / (timeInterval.endTime - timeInterval.startTime + 1)}%`;
      const _slots = [];
      const height = `${(slotPositions.slice().sort((a, b) => b - a)[0] + 1) * 7}em`;
      for (let i: number = timeInterval.startTime; i <= timeInterval.endTime; i += 1) {
        _slots.push(
          <div
            key={i}
            style={{
              width: slotWidth,
              display: 'flex',
              flexDirection: 'column',
            }}>
            <div
              className="cell"
              style={{
                position: 'relative',
                borderBottom: `2px solid ${uiComponent?.styling?.textColor || ' #CCCCCC'}`,
                height,
              }}>
              <div
                style={{
                  position: 'absolute',
                  width: '1px',
                  height: '100%',
                  background: uiComponent?.styling?.textColor || '#F2F2F2',
                  marginLeft: 'auto',
                  marginRight: 'auto',
                  left: 0,
                  right: 0,
                  textAlign: 'center',
                }}
              />
              {i !== timeInterval.endTime && (
                <div
                  style={{
                    position: 'absolute',
                    right: 0,
                    width: '1px',
                    height: '100%',
                    background: uiComponent?.styling?.textColor || '#F2F2F2',
                  }}
                />
              )}
            </div>
            <span
              style={{
                position: 'relative',
                marginLeft: 'auto',
                marginRight: 'auto',
                left: 0,
                right: 0,
                textAlign: 'center',
                bottom: 0,
              }}>
              {i}:00
            </span>
          </div>
        );
      }
      return _slots;
    }
  }

  function time2dec(time: string) {
    const _date = new Date(Date.parse(time));
    return _date.getHours() + _date.getMinutes() / 60;
  }
  function getTooltipText(slot: Slot) {
    return t('timeline-interval', {
      start: format(new Date(slot.datetime_start), 'p', { locale: i18n.language === 'en' ? enUS : de }),
      end: format(new Date(slot.datetime_end), 'p', { locale: i18n.language === 'en' ? enUS : de }),
    });
  }
  const getRandomColor = () => {
    if (event?.template.mainColor) {
      const [h, s] = colorConvert.hex.hsl(event?.template.mainColor);
      const randomToHundred = Math.floor(Math.random() * 50) + 30;
      return `hsl(${h}, ${s}%, ${randomToHundred}%)`;
    }
  };
  const handleDateChange = (date: Date | null) => {
    setSelectedDate(date);
  };
  return (
    <>
      <div className="flex flex-row justify-end">
        <DatePicker
          format="dd.MM.yyyy"
          value={selectedDate}
          onChange={handleDateChange}
          minDate={new Date(event.datetime_start)}
          maxDate={new Date(event.datetime_end)}
        />
      </div>
      <div className="flex flex-row w-full">
        {currDaySlots.length > 0 ? (
          <div className="flex relative w-full">
            {slotPositions && displaySlots()}
            {currDaySlots.map((s, i) => (
              <Tooltip
                key={'curr-day-slots-' + i}
                title={
                  <div className="flex flex-col h-full justify-between">
                    <span> {getTooltipText(s)}</span>
                  </div>
                }>
                <div
                  key={s._id}
                  className={`absolute m-2 flex justify-center items-center select-none cursor-pointer`}
                  style={{
                    width: `${(100 * (time2dec(s.datetime_end) - time2dec(s.datetime_start))) / (timeInterval.endTime - timeInterval.startTime + 1)}%`,
                    left: `${(100 * (time2dec(s.datetime_start) - timeInterval.startTime + 0.5)) / (timeInterval.endTime - timeInterval.startTime + 1)}%`,
                    bottom: `${(slotPositions ? slotPositions[i] : 0) * 6 + 4}em`,
                  }}>
                  <div
                    className="w-full h-full grid place-items-center truncate"
                    style={{
                      backgroundColor: event?.template.mainColor ? getRandomColor() : 'var(--vystem-blue)',
                      color: event?.template.mainButtonTextColor || 'white',
                    }}>
                    <b>{s.meetingroom.name}</b>
                  </div>
                </div>
              </Tooltip>
            ))}
          </div>
        ) : (
          <div className="w-full grid place-items-center p-4 m-4">
            <b>{t('slots:no-presentations')}</b>
          </div>
        )}
      </div>
    </>
  );
};

export default PresentationTimeline;
